'use client';

import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    L: any;
  }
}

// Helper function to convert tile coordinates to geographic bounds
function tileToBounds(x: number, y: number, z: number) {
  const n = Math.pow(2, z);
  
  // Calculate the northwest corner
  const lonWest = (x / n) * 360.0 - 180.0;
  const latNorth = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180.0 / Math.PI;
  
  // Calculate the southeast corner  
  const lonEast = ((x + 1) / n) * 360.0 - 180.0;
  const latSouth = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * 180.0 / Math.PI;
  
  return {
    north: latNorth,
    south: latSouth,
    west: lonWest,
    east: lonEast
  };
}

export default function WeatherMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    let cleanup: (() => void) | undefined;

    const initMap = async () => {
      // Load Leaflet CSS
      if (!document.querySelector('link[href*="leaflet"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }

      // Load Leaflet JS
      if (!window.L) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        
        script.onload = () => {
          createMap();
        };
        
        document.head.appendChild(script);
      } else {
        createMap();
      }
    };

    const createMap = () => {
      if (!mapRef.current || mapInstanceRef.current) return;

      try {
        // Initialize map with optimal view coordinates for full 3x3 grid
        const map = window.L.map(mapRef.current, {
          center: [66.3728, -0.7031], // Center coordinates showing full grid
          zoom: 2, // Zoom level 2 to show all 9 tiles in 500x500px
          zoomControl: false,
          scrollWheelZoom: false,
          doubleClickZoom: false,
          touchZoom: false,
          boxZoom: false,
          keyboard: false,
          dragging: true
        });
        
        mapInstanceRef.current = map;

        // Add base OpenStreetMap tiles
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(map);



        // Create custom tile layer using zoom level 2 tiles for full 3x3 grid view
        const WeatherTileLayer = window.L.TileLayer.extend({
          getTileUrl: function(coords: any) {
            const x = coords.x;
            const y = coords.y;
            
            // Serve tiles for zoom level 2 grid (coordinates 1-3, 0-2)
            if (x >= 1 && x <= 3 && y >= 0 && y <= 2) {
              return `/weather-maps/zoom2-grid/tile-${x}-${y}.png`;
            }
            
            // Return transparent tile for other coordinates
            return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
          }
        });

        // Add the weather tile layer
        new WeatherTileLayer({
          opacity: 0.7,
          attribution: 'Weather tiles from Tomorrow.io'
        }).addTo(map);

        // Add tile boundary markers for zoom level 2 grid
        const tileMarkers = [
          { coords: [1, 0], label: 'Tile (1,0)' },
          { coords: [2, 0], label: 'Tile (2,0)' },
          { coords: [3, 0], label: 'Tile (3,0)' },
          { coords: [1, 1], label: 'Tile (1,1)' },
          { coords: [2, 1], label: 'Tile (2,1)' },
          { coords: [3, 1], label: 'Tile (3,1)' },
          { coords: [1, 2], label: 'Tile (1,2)' },
          { coords: [2, 2], label: 'Tile (2,2)' },
          { coords: [3, 2], label: 'Tile (3,2)' }
        ];

        // Add markers for each tile - positioned at tile centers
        tileMarkers.forEach(tile => {
          const x = tile.coords[0];
          const y = tile.coords[1];
          const z = 2;
          
          // Calculate exact center of tile using proper Web Mercator projection
          const n = Math.pow(2, z);
          const centerX = x + 0.5;  // Center of tile X
          const centerY = y + 0.5;  // Center of tile Y
          
          const lon = (centerX / n) * 360.0 - 180.0;
          const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * centerY) / n)));
          const lat = latRad * 180.0 / Math.PI;
          
          window.L.marker([lat, lon], {
            icon: window.L.divIcon({
              className: 'tile-marker',
              html: `<div style="background: rgba(255,255,255,0.95); color: #333; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; border: 2px solid #666; text-align: center; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">${tile.label}</div>`,
              iconSize: [75, 26],
              iconAnchor: [37.5, 13]
            })
          }).addTo(map);
        });

        // Add grid lines to show tile boundaries
        tileMarkers.forEach(tile => {
          const x = tile.coords[0];
          const y = tile.coords[1];
          const z = 2;
          
          // Calculate tile bounds
          const tileBounds = tileToBounds(x, y, z);
          
          // Create rectangle outline for each tile
          const rectangle = window.L.rectangle([
            [tileBounds.south, tileBounds.west], // Southwest corner
            [tileBounds.north, tileBounds.east]  // Northeast corner
          ], {
            color: '#ffffff',        // White border
            weight: 2,              // 2px thick lines
            fillOpacity: 0,         // No fill, just outline
            opacity: 0.8            // Semi-transparent
          }).addTo(map);
        });

        cleanup = () => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.remove();
            mapInstanceRef.current = null;
          }
        };
      } catch (error) {
        console.error('Error initializing weather map:', error);
      }
    };

    initMap();

    // Cleanup function
    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  return (
    <div className="bg-white rounded-lg p-4 border border-blue-100">
      <div 
        ref={mapRef}
        style={{ height: '500px', width: '500px' }}
        className="rounded border border-gray-200 mx-auto"
      />
      <div className="mt-3 p-3 bg-blue-50 rounded text-xs">
        <p className="font-semibold text-blue-800 mb-2">🗺️ Weather Tile Grid Demonstration</p>
        <ul className="text-blue-700 space-y-1 mb-3">
          <li>• <strong>Pan Only:</strong> Drag to explore the tile grid system</li>
          <li>• Each marker shows the center of one 256×256 pixel tile</li>
          <li>• Weather data seamlessly spans across tile boundaries</li>
          <li>• Complete 3×3 grid: X (1-3), Y (0-2) at zoom level 2</li>
        </ul>
        <div className="p-2 bg-green-100 rounded text-green-800">
          <p className="font-semibold text-xs">💡 Key Point:</p>
          <p className="text-xs">Individual tiles combine to create continuous weather visualization</p>
        </div>
      </div>
    </div>
  );
} 